'use client'

import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Icon } from '@/app/components/Icon'
import { cn } from '@/app/utils/style'

export type DropdownItem = {
  value: string
  label: string
}

type CustomDropdownProps = {
  items: DropdownItem[]
  value?: string
  placeholder?: string
  onChange: (value: string) => void
  className?: string
  variant?: 'default' | 'small'
  portal?: boolean
}

export function CustomDropdown({
  items,
  value,
  placeholder = 'Select an option',
  onChange,
  className = '',
  variant = 'default',
  portal = false,
}: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selectedItem = items.find((item) => item.value === value)
  const sizeClass = variant === 'small' ? 'input-smallLight' : 'input-defaultLight'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!portal || !isOpen || !buttonRef.current) return

    const updatePosition = () => {
      if (!buttonRef.current) return

      const buttonRect = buttonRef.current.getBoundingClientRect()
      const maxDropdownHeight = 300
      const actualDropdownHeight = listRef.current?.offsetHeight || maxDropdownHeight
      const viewportHeight = window.innerHeight
      const spaceBelow = viewportHeight - buttonRect.bottom
      const spaceAbove = buttonRect.top
      const gap = 8

      // For fixed positioning, use viewport coordinates directly (no scrollY/scrollX offset)
      let top = buttonRect.bottom + gap
      if (spaceBelow < actualDropdownHeight && spaceAbove > spaceBelow) {
        top = buttonRect.top - actualDropdownHeight - gap
      }

      setDropdownPosition({
        top,
        left: buttonRect.left,
        width: buttonRect.width,
      })
    }

    // Initial position with a small delay to ensure dropdown is rendered and measured
    const initialUpdate = () => {
      updatePosition()
      // Double-check position after a frame to ensure accurate measurement
      requestAnimationFrame(updatePosition)
    }
    initialUpdate()

    // Use requestAnimationFrame for smoother position updates during scroll
    let rafId: number
    const handleScroll = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updatePosition)
    }

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleScroll)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleScroll)
    }
  }, [portal, isOpen])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isOutsideWrapper = dropdownRef.current && !dropdownRef.current.contains(target)
      const isOutsideList = portal && listRef.current && !listRef.current.contains(target)

      // In portal mode, check both wrapper and list. Otherwise, just check wrapper.
      if (portal ? isOutsideWrapper && isOutsideList : isOutsideWrapper) {
        setIsOpen(false)
      }
    }

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [portal])

  const handleSelect = (itemValue: string) => {
    onChange(itemValue)
    setIsOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen(!isOpen)
    }
  }

  const dropdownList = (
    <ul
      ref={listRef}
      className={cn(
        'bg-neutral-50 z-[1000] overflow-auto rounded-xs shadow-lg',
        portal ? 'fixed max-h-[300px]' : cn('min-w-full w-max mt-xs absolute'),
        isOpen ? 'block' : 'hidden',
      )}
      role="listbox"
      style={
        portal && isOpen
          ? {
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: '300px',
            }
          : undefined
      }
    >
      {items.map((item) => (
        <li
          key={item.value}
          onClick={() => handleSelect(item.value)}
          className={cn(
            'size-body-sm text-neutral-950 bg-transparent hover:bg-neon-100 active:bg-neon-200 transition-colors duration-200 cursor-pointer',
            'py-lg px-xl',
            item.value === value && 'bg-neon-200',
          )}
          role="option"
          aria-selected={item.value === value}
        >
          {item.label}
        </li>
      ))}
    </ul>
  )

  return (
    <div ref={dropdownRef} className={cn('relative w-full', className)}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        className={cn(sizeClass, 'w-full flex items-center justify-between', className)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{selectedItem ? selectedItem.label : placeholder}</span>
        <Icon
          provider="fontawesome"
          name="faChevronDown"
          size={14}
          className={cn('transition-transform', isOpen && 'rotate-180')}
        />
      </button>

      {portal && isMounted ? createPortal(dropdownList, document.body) : dropdownList}
    </div>
  )
}
